import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BookingStoreService } from 'avn/modules/features/booking-flow/booking-store.service';
import { COUNTRY_CODES, defaultCountryCode } from 'avn/modules/services/country-codes';
import { timezoneConstants, ITimezoneInfo } from 'avn/modules/services/timezone.constants';
import { ConfirmPopComponent } from 'avn/modules/shared/components/confirm-pop/confirm-pop';
import { SaveListComponent } from 'avn/modules/shared/components/save-list/save-list';
import moment from 'moment';
import * as momentz from 'moment-timezone';
import { BehaviorSubject, Subject } from 'rxjs';

export class ConfirmPopData {
  public title = 'Confirm';
  public message = 'Are you sure?';
  public cancelButtonText = 'Cancel';
  public okButtonText = 'Confirm';
  public okClassName: 'btn-success' | 'btn-warning' | 'btn-error' = 'btn-success';
  public secondaryButtons?: { name: string; class_name: string; value: string }[] = [];
  public cancelType: 'button' | 'icon' = 'button';
}

const DEFAULT_SNACKBAR_DURATION = 5;

@Injectable({
  providedIn: 'root'
})
export class SharedCompsService {
  private readonly snackBarSubject = new BehaviorSubject<any>({});
  public snackBar = this.snackBarSubject.asObservable();

  public snackBarClose = new Subject<boolean>();

  public locationDropdownUpdate = new Subject<any>();

  public pageChangeDetect = new Subject();

  public currentTimezone = '';

  public constructor(
    private readonly dialog: MatDialog,
    private readonly booking_store: BookingStoreService,
    private readonly router: Router
  ) {}

  public showSnackBar(
    message: string,
    duration = DEFAULT_SNACKBAR_DURATION,
    buttonText = 'Dismiss',
    sendBack = false,
    routerLink?: any
  ): void {
    this.snackBarSubject.next({ message, duration, buttonText, sendBack, routerLink });
  }

  public getTimezone(): string {
    if (!this.currentTimezone) {
      // localStorage is unavailable during SSR
      const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem('timezone');
      this.currentTimezone = stored || 'Australia/Melbourne';
    }
    return this.currentTimezone;
  }

  public convertedCurrentTimeWithTimezone(timezone?: string): moment.Moment {
    return momentz.tz(moment(), timezone || this.getTimezone());
  }

  public confirmPop(options: ConfirmPopData): MatDialogRef<ConfirmPopComponent> {
    const confirm = this.dialog.open(ConfirmPopComponent, {
      width: '400px',
      data: { ...options },
      disableClose: true,
      panelClass: ['confirm-pop-modal', 'popup-container']
    });

    return confirm;
  }

  public openSaveListPop(options: any): MatDialogRef<SaveListComponent> {
    const confirm = this.dialog.open(SaveListComponent, {
      width: '90%',
      maxWidth: '500px',
      maxHeight: '70%',
      data: { ...options },
      disableClose: true,
      panelClass: ['save-list-pop-modal', 'popup-container']
    });

    return confirm;
  }

  public fetchOnlyAustraliaTimezone(): string {
    let timezone = momentz.tz.guess();
    const index = timezoneConstants.timezones.findIndex((x: ITimezoneInfo) => x.timezone === timezone);
    if (index === -1) {
      timezone = 'Australia/Melbourne';
    }

    return timezone;
  }

  // convertedCurrentTimeWithTimezone() {
  //   return momentz.tz(moment(), this.fetchOnlyAustraliaTimezone());
  // }

  public getLocation(loc: any, format = ''): string {
    if (format) {
      const replacedFormat = format.replace(/{{\s*([^}]+)\s*}}/g, (_match, key: string) => {
        const value = key.split('.').reduce((acc: any, prop: string) => {
          if (acc === undefined || acc === null) {
            return undefined;
          }
          return acc[prop];
        }, loc);
        return value !== undefined && value !== null ? value : '';
      });

      return replacedFormat
        .replace(/\s+/g, ' ')
        .replace(/\s+,/g, ',')
        .replace(/,\s+/g, ', ')
        .replace(/^[\s,]+/, '')
        .replace(/[\s,]+$/, '')
        .trim();
    }
    const location = [];
    if (loc?.apartment) {
      location.push(loc?.apartment);
    }
    if (loc?.address) {
      location.push(loc?.address);
    }
    const cityName = loc?.city;
    const address = loc?.address;
    const postCode = loc?.postal_code;
    if ((cityName && !address) || (cityName && address && !address.toLowerCase().includes(cityName.toLowerCase()))) {
      location.push(cityName);
    }
    if (loc?.postal_code && !address.toLowerCase().includes(postCode.toLowerCase())) {
      location.push(loc?.postal_code);
    }
    return location.join(', ');
  }

  public convertTime(currentTime: string, extend: string, format: string): string {
    let hoursToExtend = 0;
    let minutesToExtend = 0;

    if (!isNaN(Number(extend))) {
      extend += ' mins';
    }

    const hoursToCheck = extend.toLowerCase().split('hr');
    if (!isNaN(Number(hoursToCheck[0]))) {
      hoursToExtend = Number(hoursToCheck[0]);
      extend = hoursToCheck[1];
    }
    const minutesToCheck = extend.toLowerCase().split('mins');
    if (!isNaN(Number(minutesToCheck[0]))) {
      minutesToExtend = Number(minutesToCheck[0]);
    }

    return moment(currentTime, 'hh:mm A')
      .add(minutesToExtend, 'minutes')
      .add(hoursToExtend, 'hours')
      .format(format || 'HH:mm');
  }

  public getCountryCodeFromPhone(phone: string): string {
    const hasCode = phone?.includes('+');
    if (hasCode) {
      const listFilter = COUNTRY_CODES.filter((x: any) => phone.includes(x.code));
      return listFilter?.[0]?.code || defaultCountryCode;
    }
    return defaultCountryCode;
  }

  public goToBooking(bookingSlug: {
    business: string;
    service: string;
    prac?: string | number;
    date?: string;
    time?: string;
    goto?: string;
    return?: 'navigate' | 'return';
  }): string[] | undefined {
    const urlSplit = this.router.url.split('?');
    sessionStorage.setItem('path-from', urlSplit[0]);
    if (urlSplit.length > 1) {
      sessionStorage.setItem('path-query', urlSplit[1]);
    }
    this.booking_store.resetBookingData();
    let url = `/booking/${bookingSlug.business}/${bookingSlug.service}`;
    if (bookingSlug.prac) {
      url += `/${String(bookingSlug.prac)}`;
    }
    if (bookingSlug.date) {
      url += `/${bookingSlug.date}`;
    }
    if (bookingSlug.time) {
      url += `/${bookingSlug.time.replace(/ /g, '')}`;
    }
    if (bookingSlug.goto) {
      url += `/${bookingSlug.goto}`;
    }

    if (bookingSlug.return === 'return') {
      return [url];
    } else {
      let queryParams: any = {};
      queryParams = this.parseQueryString(sessionStorage.getItem('path-query') || '');
      this.router.navigate([url], {
        queryParams
      });
      return;
    }
  }

  public parseQueryString(query: string): Record<string, string | number> {
    if (query.includes('?')) {
      query = query.split('?')[1];
    }
    const params = new URLSearchParams(query);
    const result: any = {};

    params.forEach((value, key) => {
      // Convert numeric strings to actual numbers if possible
      const numberValue = Number(value);
      result[key] = isNaN(numberValue) ? value : numberValue;
    });

    return result;
  }
}
